/**
 * /timestamp-camera — the page.
 *
 * A photo here gets TWO times and says which is which:
 *   • the verified time — our server's clock at the moment it signed the
 *     photo's fingerprint (ECDSA P-256; anyone checks it with the public key);
 *   • the phone's own clock — printed next to it with how far it is off, so a
 *     phone set to the wrong hour or zone never puts a wrong time on a photo
 *     and never leaves anyone guessing which time is which.
 * The place is the device's word and is labelled as such; the weather is the
 * forecast model's value for that hour, labelled as such.
 *
 * Flow of one photo: picture at output size → perceptual hash of its top
 * 70 % → /stamp (our time, signed) → the band and the QR drawn into the
 * bottom → JPEG → SHA-256 → /seal (the file, signed) → both stamps written
 * into an APP11 segment. Without a network the photo waits in the roll
 * (work storage) and is confirmed later, honestly as «not later than».
 *
 * Unit of the daily cap: the finished PDF report (Farid 20.10.2026); a
 * single stamped photo and every check are never counted.
 */

import { Camera, withTimeout } from '../online-camera/camera.js';
import { withExif } from '../online-camera/exif.js';
import * as T from './stamp-token.js';
import * as F from './fingerprint.js';
import { baseCanvas, drawBand, MAX_SIDE } from './overlay.js';
import { loadQr, encode as qrEncode } from './qr.js';
import { Roll, newId } from './store.js';
import { build as buildPdf, loadPdfLib } from './pdf.js';
import * as V from './verify.js';

const C = window.__TSC_CFG || {};
const SLUG = 'timestamp-camera';
const LATE_MS = 60 * 1000;            // confirmed more than a minute after the shot → «not later than»
const BIG_SKEW_MS = 2 * 60 * 1000;    // phone clock off by more than this → say why it does not matter
const $ = (id) => document.getElementById(id);

function t(k, rep) {
    let s = (C.t && C.t[k]) || k;
    if (rep) for (const n in rep) s = s.split(':' + n).join(rep[n] == null ? '' : String(rep[n]));
    return s;
}
const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const loc = () => (window.__intlLocale ? window.__intlLocale() : (C.locale || 'en'));
const stage = (n, m) => { try { window.__stage && window.__stage(n, m || {}); } catch (_) { } };
const toast = (m, type) => { try { window.__toast && window.__toast(m, type || 'info'); } catch (_) { } };

// ── formatting ────────────────────────────────────────────────────────────

/** Time of `ms` on a clock `off` minutes east of UTC. */
function fmtTime(ms, off) {
    try { return new Intl.DateTimeFormat(loc(), { timeZone: 'UTC', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' }).format(new Date(ms + off * 60000)); }
    catch (_) { return new Date(ms + off * 60000).toISOString().slice(11, 19); }
}
function fmtDate(ms, off) {
    try { return new Intl.DateTimeFormat(loc(), { timeZone: 'UTC', day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(ms + off * 60000)); }
    catch (_) { return new Date(ms + off * 60000).toISOString().slice(0, 10); } // utc-calendar: shifted by the photo's own zone first
}
function fmtTz(off) {
    const s = off < 0 ? '−' : '+', a = Math.abs(off), h = Math.floor(a / 60), m = a % 60;
    return 'UTC' + s + h + (m ? ':' + String(m).padStart(2, '0') : '');
}
const fmtUtc = (ms) => new Date(ms).toISOString().replace('T', ' ').slice(0, 19) + ' UTC';
function unit(n, u) {
    try { return new Intl.NumberFormat(loc(), { style: 'unit', unit: u, unitDisplay: 'short', maximumFractionDigits: 0 }).format(n); }
    catch (_) { return n + ' ' + u; }
}
/** «3 мин 12 с» — two largest units. */
function fmtDur(ms) {
    let s = Math.round(Math.abs(ms) / 1000);
    const parts = [['day', 86400], ['hour', 3600], ['minute', 60], ['second', 1]];
    const out = [];
    for (const [u, k] of parts) {
        if (s >= k || (u === 'second' && !out.length)) { const n = Math.floor(s / k); s -= n * k; out.push(unit(n, u)); }
        if (out.length === 2) break;
    }
    return out.join(' ');
}
/** The phone clock against ours, in words. skew > 0 — the phone is ahead. */
function skewText(skew) {
    if (Math.abs(skew) < 1500) return t('skew_ok');
    return t(skew > 0 ? 'skew_ahead' : 'skew_behind', { d: fmtDur(skew) });
}
function coords(g) {
    const n = (v) => v.toFixed(5);
    let s = n(g.lat) + ', ' + n(g.lon);
    if (g.acc != null && g.acc < 65535) s += ' ±' + unit(g.acc, 'meter');
    return s;
}

// ── clock: how far the phone is from us ───────────────────────────────────

const clock = { offset: null, rtt: null, at: 0, failed: false };

let pingRetry = null, pingWait = 5000;
async function ping() {
    if (pingRetry) { clearTimeout(pingRetry); pingRetry = null; }
    let best = null;
    for (let i = 0; i < 3; i++) {
        try {
            const d0 = Date.now(), p0 = performance.now();
            const r = await fetchBounded(C.urls.time, { cache: 'no-store' }, 12000);
            const j = await r.json();
            const rtt = performance.now() - p0;
            if (!best || rtt < best.rtt) best = { rtt, offset: j.now - (d0 + rtt / 2) };
        } catch (_) { if (!best) break; }
    }
    if (best) { clock.offset = best.offset; clock.rtt = best.rtt; clock.at = Date.now(); clock.failed = false; pingWait = 5000; }
    else {
        clock.failed = true;
        // no answer: ask again soon, then less and less often — a page that
        // gave up for five minutes after one slow reply showed «no connection»
        pingRetry = setTimeout(ping, pingWait);
        pingWait = Math.min(pingWait * 2, 120000);
    }
    paintClock();
}

function textWrite(e, t) { if (window.__setText) return window.__setText(e, t); e.textContent = t; }

function paintClock() {
    const now = $('tscNow'), sk = $('tscSkew');
    if (!now) return;
    if (clock.offset == null) {
        textWrite(now, clock.failed ? '—' : '…');
        sk.textContent = clock.failed ? t('clock_off') : t('clock_wait');
        return;
    }
    const off = -new Date().getTimezoneOffset();
    const txt = fmtTime(Date.now() + clock.offset, off);
    textWrite(now, txt);
    const s = skewText(-clock.offset);
    if (sk.textContent !== s) sk.textContent = s;
}

// ── network ───────────────────────────────────────────────────────────────

function fetchBounded(url, opts, ms) {
    const ctl = new AbortController();
    const stop = (window.__visibleTimeout || ((f, d) => { const h = setTimeout(f, d); return () => clearTimeout(h); }))(() => ctl.abort(), ms);
    return fetch(url, Object.assign({}, opts, { signal: ctl.signal })).finally(() => { try { stop && stop(); } catch (_) { } });
}
function csrf() { const m = document.querySelector('meta[name="csrf-token"]'); return m ? m.getAttribute('content') || '' : ''; }

class NetError extends Error { constructor(m) { super(m); this.code = 'NET'; } }

async function post(url, body, ms = 15000) {
    let r;
    try {
        r = await fetchBounded(url, {
            method: 'POST', credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-TOKEN': csrf() },
            body: JSON.stringify(body),
        }, ms);
    } catch (e) { throw new NetError(String((e && e.name) || e)); }
    if (r.status === 419) { const e = new Error('session'); e.code = 'SESSION'; throw e; }
    if (r.status === 429 || r.status >= 500) throw new NetError('http_' + r.status);
    const j = await r.json().catch(() => null);
    if (!r.ok || !j || j.ok === false) { const e = new Error((j && j.error) || ('http_' + r.status)); e.code = 'REFUSED'; throw e; }
    return j;
}

async function serverVerify(bytes) {
    const j = await post(C.urls.verify, { token: T.b64urlEncode(bytes) });
    return { valid: !!j.valid, known: !!j.known_key, supported: true };
}

// ── place and weather (best effort, bounded) ──────────────────────────────

const geo = { watch: null, last: null, state: '' };

function startGeo() {
    if (!$('tscOptPlace').checked) { stopGeo(); paintGeo(); return; }
    if (!navigator.geolocation) { geo.state = 'none'; paintGeo(); return; }
    if (geo.watch != null) return;
    geo.state = 'wait'; paintGeo();
    try {
        geo.watch = navigator.geolocation.watchPosition((p) => {
            geo.last = { lat: p.coords.latitude, lon: p.coords.longitude, acc: Math.round(p.coords.accuracy || 0), ts: p.timestamp || Date.now() };
            geo.state = 'ok'; paintGeo();
        }, (err) => {
            geo.state = err && err.code === 1 ? 'denied' : (geo.last ? 'ok' : 'none');
            paintGeo();
        }, { enableHighAccuracy: true, maximumAge: 15000, timeout: 30000 });
    } catch (_) { geo.state = 'none'; paintGeo(); }
}
function stopGeo() {
    if (geo.watch != null) { try { navigator.geolocation.clearWatch(geo.watch); } catch (_) { } }
    geo.watch = null;
}
function paintGeo() {
    const el = $('tscGeoNote');
    if (!el) return;
    const on = $('tscOptPlace').checked;
    let s = '';
    if (on) {
        if (geo.state === 'denied') s = t('geo_denied');
        else if (geo.state === 'none') s = t('geo_none');
        else if (geo.state === 'wait' && !geo.last) s = t('geo_wait');
        else if (geo.last) s = t('geo_ok', { p: coords(geo.last) });
    }
    el.textContent = s;
    el.hidden = !s;
}
function currentGeo() {
    if (!$('tscOptPlace').checked || !geo.last) return null;
    const age = Math.max(0, Math.round((Date.now() - geo.last.ts) / 1000));
    if (age > 600) return null;                    // a fix ten minutes old is somewhere else
    return Object.assign({ age, src: 'device' }, geo.last);
}

const placeCache = new Map();
async function placeLabel(g) {
    if (!g || !window.GeoCity) return '';
    const key = g.lat.toFixed(3) + ',' + g.lon.toFixed(3);
    if (placeCache.has(key)) return placeCache.get(key);
    try {
        const it = await withTimeout(window.GeoCity.lookup(g.lat, g.lon, { mode: 'near' }), 4000);
        const s = window.GeoCity.text(it, { distance: true }) || '';
        placeCache.set(key, s);
        return s;
    } catch (_) { return ''; }
}

const wxCache = new Map();
async function weatherText(g) {
    if (!g || !C.weatherUrl) return '';
    const key = g.lat.toFixed(2) + ',' + g.lon.toFixed(2);
    let data = wxCache.get(key);
    if (!data || Date.now() - data.at > 30 * 60000) {
        try {
            const r = await fetchBounded(C.weatherUrl + '?lat=' + g.lat.toFixed(2) + '&lon=' + g.lon.toFixed(2), {}, 5000);
            const j = await r.json();
            data = { at: Date.now(), hours: Array.isArray(j.hours) ? j.hours : [] };
            wxCache.set(key, data);
        } catch (_) { return ''; }
    }
    const now = Date.now();
    const h = data.hours.find((x) => { const s = Date.parse(x.t); return s <= now && now < s + (x.span || 1) * 3600e3; });
    if (!h) return '';
    const parts = [];
    const sym = symLabel(h.sym);
    if (sym) parts.push(sym);
    if (typeof h.temp === 'number') parts.push((h.temp > 0 ? '+' : '') + Math.round(h.temp) + ' °C' + (C.locale === 'en' ? ' (' + Math.round(h.temp * 9 / 5 + 32) + ' °F)' : ''));
    if (typeof h.wind === 'number') parts.push(t('wx_wind', { v: unit(Math.round(h.wind), 'meter-per-second') }));
    return parts.length ? t('band_weather', { w: parts.join(', ') }) : '';
}
function symLabel(sym) {
    if (!sym || !C.sym) return '';
    let s = String(sym).replace(/_(day|night|polartwilight)$/, '');
    const thunder = /andthunder$/.test(s);
    s = s.replace(/andthunder$/, '').replace(/showers$/, '');
    const base = C.sym['sym_' + s] || C.sym.sym_cloudy || '';
    return thunder && C.sym.sym_with_thunder ? C.sym.sym_with_thunder.replace(':c', base) : base;
}

// ── the roll ──────────────────────────────────────────────────────────────

const roll = new Roll();
let recs = [];
const urls = new Map();
const confirming = new Set();

function thumbUrl(r) {
    const blob = r.thumb || r.file || r.raw;
    const k = r.id + ':' + (r.status) + ':' + (blob ? blob.size : 0);
    if (urls.has(r.id) && urls.get(r.id).k === k) return urls.get(r.id).u;
    if (urls.has(r.id)) URL.revokeObjectURL(urls.get(r.id).u);
    const u = blob ? URL.createObjectURL(blob) : '';
    urls.set(r.id, { k, u });
    return u;
}

function cellHtml(r) {
    const st = r.status === 'ok' ? '<span class="tsc-st ok">' + esc(t('st_ok')) + '</span>'
        : r.status === 'pending' ? '<span class="tsc-st wait">' + esc(t('st_pending')) + '</span>'
            : '<span class="tsc-st bad">' + esc(t('st_error')) + '</span>';
    const when = r.status === 'ok' ? (r.late || r.mode === 'gallery' ? t('cell_not_later', { t: fmtTime(r.serverMs, r.tzOff) }) : fmtTime(r.serverMs, r.tzOff)) + ' · ' + fmtDate(r.serverMs, r.tzOff)
        : fmtTime(r.ts, r.tzOff) + ' · ' + t('cell_phone_clock');
    const role = (v, lbl) => '<button type="button" class="tsc-chip' + (r.role === v ? ' on' : '') + '" data-act="role" data-v="' + v + '" aria-pressed="' + (r.role === v) + '">' + esc(lbl) + '</button>';
    return '<div class="tsc-cell' + (r.sel ? ' sel' : '') + '" data-id="' + esc(r.id) + '">'
        + '<button type="button" class="tsc-pic" data-act="sel" aria-pressed="' + (!!r.sel) + '" aria-label="' + esc(t('select')) + '">'
        + '<img alt="" loading="lazy" src="' + esc(thumbUrl(r)) + '"><span class="tsc-tick" aria-hidden="true"></span></button>'
        + '<div class="tsc-meta">' + st + '<span class="tsc-when">' + esc(when) + '</span>'
        + (r.code ? '<span class="tsc-code">' + esc(r.code) + '</span>' : '')
        + (r.note ? '<span class="tsc-note-v">' + esc(r.note) + '</span>' : '') + '</div>'
        + '<div class="tsc-acts">' + role('before', t('role_before')) + role('after', t('role_after'))
        + '<button type="button" class="tsc-chip" data-act="note">' + esc(t('note')) + '</button>'
        + '<button type="button" class="tsc-chip" data-act="dl"' + (r.status === 'ok' ? '' : ' disabled') + '>' + esc(t('dl')) + '</button>'
        + (r.status === 'error' ? '<button type="button" class="tsc-chip" data-act="retry">' + esc(t('retry')) + '</button>' : '')
        + '<button type="button" class="tsc-chip danger" data-act="del" aria-label="' + esc(t('del')) + '">✕</button>'
        + '</div></div>';
}

function renderRoll() {
    const g = $('tscGrid');
    const list = recs.slice().reverse();
    g.innerHTML = list.map(cellHtml).join('');
    $('tscRollEmpty').hidden = list.length > 0;
    $('tscRollCount').textContent = list.length ? String(list.length) : '';
    $('tscDlAll').disabled = !list.some((r) => r.status === 'ok');
    $('tscDelAll').disabled = !list.length;
    $('tscSelAll').disabled = !list.length;
    const sel = recs.filter((r) => r.sel && r.status === 'ok').length;
    $('tscSelCount').textContent = sel ? '(' + sel + ')' : '';
    const last = list[0];
    const lb = $('tscLast');
    lb.disabled = !last;
    lb.style.backgroundImage = last ? 'url("' + thumbUrl(last) + '")' : '';
    paintReport();
}

async function loadRoll() {
    await roll.open();
    $('tscRollMem').hidden = roll.durable;
    recs = await roll.list();
    renderRoll();
    retryPending();
}

async function saveRec(r) {
    const i = recs.findIndex((x) => x.id === r.id);
    if (i < 0) recs.push(r); else recs[i] = r;
    await roll.put(r);
    if (roll.durable && window.__workStorage) { try { window.__workStorage.protect(SLUG); } catch (_) { } }
}

function fileName(r) {
    const d = new Date((r.serverMs || r.ts) + r.tzOff * 60000).toISOString();
    return 'photo-' + d.slice(0, 10) + '-' + d.slice(11, 19).replace(/:/g, '') + (r.code ? '-' + r.code : '') + '.jpg';
}

// ── one photo: picture → stamp → band → seal ──────────────────────────────

function cloneCanvas(c) {
    const o = document.createElement('canvas');
    o.width = c.width; o.height = c.height;
    o.getContext('2d').drawImage(c, 0, 0);
    return o;
}
async function makeThumb(c) {
    const k = Math.min(1, 360 / Math.max(c.width, c.height));
    const o = document.createElement('canvas');
    o.width = Math.max(1, Math.round(c.width * k)); o.height = Math.max(1, Math.round(c.height * k));
    const x = o.getContext('2d');
    x.imageSmoothingQuality = 'high';
    x.drawImage(c, 0, 0, o.width, o.height);
    return window.__canvasBlob(o, 'image/jpeg', 0.8);
}
async function bitmapOf(blob) {
    try { return await createImageBitmap(blob, { imageOrientation: 'from-image' }); }
    catch (_) {
        return await new Promise((resolve, reject) => {
            const u = URL.createObjectURL(blob), img = new Image();
            img.onload = () => { URL.revokeObjectURL(u); resolve(img); };
            img.onerror = () => { URL.revokeObjectURL(u); const e = new Error('decode'); e.code = 'DECODE'; reject(e); };
            img.src = u;
        });
    }
}
async function canvasOf(blob) {
    const b = await bitmapOf(blob);
    const c = document.createElement('canvas');
    c.width = b.width || b.naturalWidth; c.height = b.height || b.naturalHeight;
    c.getContext('2d').drawImage(b, 0, 0);
    try { b.close && b.close(); } catch (_) { }
    return c;
}

function bandLines(r, cap) {
    const off = r.tzOff, S = cap.serverMs;
    const notLater = cap.late || cap.gallery;
    const place = r.geo ? [r.place, coords(r.geo)].filter(Boolean).join(' · ') : '';
    const footer = [];
    if (cap.gallery) footer.push(t('band_footer_gallery', { e: r.exif ? r.exif.text : t('none') }));
    else if (cap.late) footer.push(t('band_footer_late', { d: fmtTime(r.ts, off) + ' ' + fmtDate(r.ts, off) }));
    else footer.push(t('band_footer_live'));
    if (!cap.gallery && Math.abs(cap.skewMs) >= BIG_SKEW_MS) footer.push(skewText(cap.skewMs));
    footer.push(C.host + ' · ' + cap.code);
    return {
        time: notLater ? t('band_not_later', { t: fmtTime(S, off) }) : fmtTime(S, off),
        date: fmtDate(S, off), tz: fmtTz(off),
        place: place ? (r.geo.src === 'exif' ? t('band_place_file', { p: place }) : place) : '',
        caption: [r.object, r.caption].filter(Boolean).join(' — '),
        weather: r.weather || '',
        footer: footer.join(' · '),
        status: 'ok', rtl: !!C.rtl,
    };
}

async function stampBody(r, canvas) {
    const late = r.mode === 'live' && Date.now() - r.ts > LATE_MS;
    const b = {
        dhash: F.toHex(F.dhashOf(canvas, canvas.width, canvas.height)),
        mode: r.mode, late: late ? 1 : 0, device_ms: r.ts, sent_ms: Date.now(),
        rtt_ms: clock.rtt != null ? Math.min(65535, Math.round(clock.rtt)) : null, tz_off: r.tzOff,
        // our estimate of device − server from the /time pings; the server
        // keeps it only if it agrees with what the server itself can bound
        skew_ms: clock.offset != null && Date.now() - clock.at < 15 * 60000 ? Math.round(-clock.offset) : null,
    };
    if (r.geo) { b.lat = r.geo.lat; b.lon = r.geo.lon; b.acc = r.geo.acc; b.geo_age = r.geo.age || 0; }
    if (r.exif && r.exif.local) { b.exif_local = r.exif.local; if (r.exif.off != null) b.exif_off = r.exif.off; }
    return b;
}

async function confirm(r, canvas) {
    if (confirming.has(r.id)) return;
    confirming.add(r.id);
    try {
        if (!canvas) canvas = await canvasOf(r.raw);
        // The stamp needs only the picture's fingerprint, so it goes first; the
        // place name, the weather and the QR encoder are fetched beside it and
        // get a bounded wait — a slow lookup must not delay the time itself.
        const stampP = post(C.urls.stamp, await stampBody(r, canvas));
        const extras = Promise.all([
            placeLabel(r.geo),
            r.showWeather && r.geo ? weatherText(r.geo) : Promise.resolve(''),
            r.showQr ? loadQr().then(() => true, () => false) : Promise.resolve(false),
        ]);
        const a = await stampP;
        const [place, wx, qrOk] = await withTimeout(extras, 2500).catch(() => ['', '', !!window.qrcode]);
        r.place = place || ''; r.weather = wx || '';
        const capBytes = T.b64urlDecode(a.token);
        const cap = T.decode(capBytes);
        const out = cloneCanvas(canvas);
        let qr = null;
        if (r.showQr) {
            if (!qrOk) await loadQr();
            qr = qrEncode(C.verifyBase + '#', T.fragmentFor(capBytes));
        }
        drawBand(out, bandLines(r, cap), qr);
        const jpg = await window.__canvasBlob(out, 'image/jpeg', 0.9);
        const exifDate = r.exif && r.exif.date ? r.exif.date : new Date(cap.serverMs);
        const withMeta = await withExif(jpg, { date: exifDate, width: out.width, height: out.height, software: 'Timbrica Timestamp Camera' });
        const bytes = new Uint8Array(await withMeta.arrayBuffer());
        const sha = F.toHex(await F.sha256(bytes));
        const b = await post(C.urls.seal, { token: a.token, sha256: sha });
        const sealBytes = T.b64urlDecode(b.token);
        const final = F.insertSegment(bytes, F.packPayload(capBytes, sealBytes));
        Object.assign(r, {
            status: 'ok', file: new Blob([final], { type: 'image/jpeg' }), capture: capBytes, seal: sealBytes,
            code: cap.code, serverMs: cap.serverMs, skewMs: cap.skewMs, late: cap.late, sealMs: T.decode(sealBytes).serverMs, sha,
            err: null,
        });
        r.thumb = await makeThumb(out);
        await saveRec(r);
        stage('confirmed', { mode: r.mode, late: cap.late ? 1 : 0, qr: qr ? 1 : 0, geo: r.geo ? 1 : 0 });
        if (r.quiet !== true) toast(t('toast_ok', { t: fmtTime(cap.serverMs, r.tzOff) }), 'success');
    } catch (e) {
        if (e && e.code === 'NET') {
            if (r.status !== 'pending') { r.status = 'pending'; toast(t('toast_pending'), 'warning'); }
            // a failed attempt must not wait for an «online» event that may never come
            // (the network was there, the server was slow): ask again on the timer
            if (!retryTimer) retryTimer = setTimeout(retryPending, 30000);
        } else {
            r.status = 'error'; r.err = String((e && (e.code || e.message)) || e).slice(0, 60);
            if (e && e.code === 'SESSION') toast(t('err_session'), 'error');
            else if (window.__handleError) window.__handleError(e, 'tsc_confirm', { say: t('err_confirm') });
        }
        await saveRec(r);
    } finally {
        confirming.delete(r.id);
        renderRoll();
    }
}

let retryTimer = null;
function retryPending() {
    if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; }
    const waiting = recs.filter((r) => r.status === 'pending');
    if (!waiting.length) return;
    if (navigator.onLine !== false && document.visibilityState !== 'hidden') {
        (async () => { for (const r of waiting) { r.quiet = true; await confirm(r); r.quiet = false; } })();
    }
    retryTimer = setTimeout(retryPending, 30000);
}

async function processShot(src, sw, sh, o) {
    const base = baseCanvas(src, sw, sh, MAX_SIDE);
    const r = {
        id: newId(), ts: o.deviceMs, mode: o.mode, status: 'pending', name: o.name || '',
        raw: await window.__canvasBlob(base, 'image/jpeg', 0.92), w: base.width, h: base.height,
        tzOff: -new Date(o.deviceMs).getTimezoneOffset(), geo: o.geo || null, exif: o.exif || null,
        object: $('tscObject').value.trim().slice(0, 120), caption: $('tscCaption').value.trim().slice(0, 160),
        showWeather: $('tscOptWeather').checked, showQr: $('tscOptQr').checked,
        note: '', role: '', sel: true,
    };
    r.thumb = await makeThumb(base);
    await saveRec(r);
    renderRoll();
    stage('shot', { mode: o.mode });
    await confirm(r, base);
}

// ── camera ────────────────────────────────────────────────────────────────

let cam = null, facing = 'environment', busy = false;

async function openCamera(btn) {
    $('tscFail').hidden = true;
    try {
        if (!cam) cam = new Camera({ video: $('tscVideo'), slug: SLUG });
        await cam.open({ facingMode: facing, res: { w: 1920, h: 1080 }, button: btn });
        $('tscIdle').hidden = true;
        $('tscShutter').disabled = false;
        const devs = await cam.devices();
        $('tscFlip').disabled = devs.filter((d) => d.kind === 'videoinput').length < 2;
        $('tscInfo').textContent = t('info_size', { w: cam.width, h: cam.height });
        startGeo();
        stage('camera_on', {});
    } catch (e) {
        $('tscFailText').textContent = cam ? cam.errorMessage(e) : t('err_camera');
        $('tscFail').hidden = false;
        $('tscIdle').hidden = true;
        $('tscShutter').disabled = true;
    }
}

async function shoot() {
    if (busy || !cam || !cam.live) return;
    busy = true;
    $('tscShutter').disabled = true;
    try {
        const deviceMs = Date.now();
        const f = $('tscFlash');
        f.classList.remove('on'); void f.offsetWidth; f.classList.add('on');
        const geoNow = currentGeo();
        let src = cam.grabFrame();
        if (!src) { toast(t('err_no_frame'), 'error'); return; }
        let sw = src.width, sh = src.height;
        // the camera's photo mode only when it promises a bigger picture than the stream
        const big = cam.canTakePhoto() && cam.stillSize();
        if (big && big.w * big.h > sw * sh * 1.2) {
            const still = await withTimeout(cam.takeStill({}), 3000).catch(() => null);
            if (still && still.width * still.height > sw * sh * 1.2 && (still.width > still.height) === (sw > sh)) { src = still; sw = still.width; sh = still.height; }
        }
        await processShot(src, sw, sh, { mode: 'live', deviceMs, geo: geoNow });
    } catch (e) {
        if (window.__handleError) window.__handleError(e, 'tsc_shoot', { say: t('err_confirm') });
    } finally {
        busy = false;
        $('tscShutter').disabled = !(cam && cam.live);
    }
}

// ── gallery ───────────────────────────────────────────────────────────────

function pad2(n) { return String(n).padStart(2, '0'); }
async function readExif(file) {
    if (!window.exifr) return { exif: null, geo: null };
    let exif = null, g = null;
    try {
        const tags = await window.exifr.parse(file, { pick: ['DateTimeOriginal', 'OffsetTimeOriginal', 'CreateDate'], reviveValues: false });
        const raw = tags && (tags.DateTimeOriginal || tags.CreateDate);
        const m = raw && /^(\d{4}):(\d{2}):(\d{2}) (\d{2}):(\d{2}):(\d{2})/.exec(String(raw));
        if (m) {
            const off = tags.OffsetTimeOriginal && /^([+-])(\d{2}):(\d{2})$/.exec(String(tags.OffsetTimeOriginal));
            exif = {
                local: m[1] + '-' + m[2] + '-' + m[3] + 'T' + m[4] + ':' + m[5] + ':' + m[6],
                off: off ? (off[1] === '-' ? -1 : 1) * (parseInt(off[2], 10) * 60 + parseInt(off[3], 10)) : null,
                date: new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]),
            };
            exif.text = m[3] + '.' + m[2] + '.' + m[1] + ' ' + m[4] + ':' + m[5] + (exif.off != null ? ' ' + fmtTz(exif.off) : '');
        }
    } catch (_) { }
    try {
        const p = await window.exifr.gps(file);
        if (p && isFinite(p.latitude) && isFinite(p.longitude)) g = { lat: p.latitude, lon: p.longitude, acc: 65535, age: 0, src: 'exif' };
    } catch (_) { }
    return { exif, geo: g };
}

// HEIC/HEIF (фото iPhone). Safari декодирует его сам; Chrome, Edge и Firefox — нет,
// и до 22.10.2026 такое фото получало «сохраните как JPEG». Теперь сначала пробуем
// родной декод (Safari не платит ничего), а при отказе — общий пул HeicConvert
// (GPU через WebCodecs, иначе libheif в воркере), который грузится только здесь.
// Exif читается из ИСХОДНОГО файла (exifr понимает HEIC), поэтому конвертеру
// Exif не нужен, а JPEG выходит уже повёрнутым — второго поворота нет.
const HEIC_BRANDS = /^(heic|heix|hevc|hevx|heim|heis|hevm|hevs|mif1|msf1)$/;
async function isHeic(file) {
    if (/^image\/hei[cf]/i.test(file.type || '') || /\.(heic|heif)$/i.test(file.name || '')) return true;
    try {
        const b = new Uint8Array(await file.slice(0, 12).arrayBuffer());
        return String.fromCharCode(b[4], b[5], b[6], b[7]) === 'ftyp' && HEIC_BRANDS.test(String.fromCharCode(b[8], b[9], b[10], b[11]));
    } catch (_) { return false; }
}
let heicPool = null;
async function heicPoolReady() {
    if (!heicPool) {
        heicPool = (async () => {
            if (!window.HeicConvert && C.lazy && C.lazy.heicConvert) await window.__vendorScript(C.lazy.heicConvert, { expect: 'HeicConvert', label: 'heic-convert' });
            if (!window.HeicConvert || !C.heic) throw Object.assign(new Error('heic_convert_unavailable'), { code: 'HEIC_FAILED' });
            // Галерея идёт по одному фото, второй воркер — только лишняя память.
            const pool = window.HeicConvert.create({ workerUrl: C.heic.worker, heicToUrl: C.heic.heicTo, workers: 1 });
            const caps = await pool.ready;
            try { window.__ae && window.__ae('feature_use', { slug: SLUG, f: 'heic_engine', lane: caps.lane, webcodecs: caps.webcodecs }); } catch (_) { }
            return pool;
        })();
        heicPool.catch(() => { heicPool = null; });
    }
    return heicPool;
}
async function heicBitmap(file) {
    toast(t('heic_opening', { name: file.name }), 'info');
    try {
        const pool = await heicPoolReady();
        const { blob } = await pool.convert(file, { format: 'image/jpeg', quality: 0.92, keepExif: false });
        return await bitmapOf(blob);
    } catch (e) {
        // The worker's own code (WORKER_DIED, a libheif message…) stays for telemetry;
        // what the person reads is decided by `heic`.
        const err = e instanceof Error ? e : new Error(String(e));
        err.heic = true;
        throw err;
    }
}

async function addFromGallery(files) {
    if (!window.exifr && C.lazy && C.lazy.exifr) { try { await window.__vendorScript(C.lazy.exifr, { expect: 'exifr', label: 'exifr' }); } catch (_) { } }
    for (const file of files) {
        if (!file || !/^image\//.test(file.type || 'image/')) continue;
        try {
            const { exif, geo: g } = await readExif(file);
            let bmp;
            try { bmp = await bitmapOf(file); }
            catch (e) {
                if (!(e && e.code === 'DECODE' && await isHeic(file))) throw e;
                bmp = await heicBitmap(file);
            }
            await processShot(bmp, bmp.width || bmp.naturalWidth, bmp.height || bmp.naturalHeight, { mode: 'gallery', deviceMs: Date.now(), geo: g, exif, name: file.name });
            try { bmp.close && bmp.close(); } catch (_) { }
        } catch (e) {
            if (e && e.code === 'DECODE') toast(t('err_decode', { name: file.name }), 'error');
            else if (e && (e.heic || e.code === 'HEIC_FAILED')) {
                if (window.__handleError) window.__handleError(e, 'tsc_heic', { say: t('err_heic', { name: file.name }) });
                else toast(t('err_heic', { name: file.name }), 'error');
            }
            else if (window.__handleError) window.__handleError(e, 'tsc_gallery', { say: t('err_confirm') });
        }
    }
}

// ── report ────────────────────────────────────────────────────────────────

function selected() { return recs.filter((r) => r.sel && r.status === 'ok').sort((a, b) => a.serverMs - b.serverMs); }

function paintReport() {
    const sel = selected();
    const waiting = recs.filter((r) => r.sel && r.status !== 'ok').length;
    $('tscRepSel').textContent = sel.length ? t('rep_sel', { n: sel.length }) : t('rep_sel_none');
    $('tscRepWait').hidden = !waiting;
    $('tscRepWait').textContent = waiting ? t('rep_pending_skip', { n: waiting }) : '';
    $('tscRepMax').hidden = sel.length <= (C.reportMax || 60);
    $('tscPdfBtn').disabled = !sel.length || sel.length > (C.reportMax || 60);
}

function factsFor(r) {
    const cap = T.decode(r.capture);
    const off = r.tzOff, f = [];
    f.push([t('f_code'), r.code]);
    const tm = fmtTime(cap.serverMs, off) + ' · ' + fmtDate(cap.serverMs, off) + ' (' + fmtTz(off) + ')';
    f.push([t('f_time'), cap.gallery ? t('f_time_gallery', { t: tm }) : cap.late ? t('f_time_late', { t: tm }) : t('f_time_live', { t: tm })]);
    f.push([t('f_utc'), fmtUtc(cap.serverMs)]);
    if (!cap.gallery) f.push([t('f_phone'), fmtTime(cap.deviceMs, off) + ' · ' + fmtDate(cap.deviceMs, off) + ' — ' + skewText(cap.skewMs)]);
    if (cap.exifMs != null && r.exif) f.push([t('f_exif'), t('f_exif_v', { e: r.exif.text })]);
    if (r.geo) f.push([t('f_place'), t(r.geo.src === 'exif' ? 'f_place_file' : 'f_place_device', { p: [coords(r.geo), r.place].filter(Boolean).join(' · ') })]);
    if (r.weather) f.push([t('f_weather'), r.weather]);
    if (r.object) f.push([t('f_object'), r.object]);
    if (r.caption) f.push([t('f_caption'), r.caption]);
    if (r.note) f.push([t('f_note'), r.note]);
    f.push([t('f_file'), 'SHA-256 ' + r.sha.slice(0, 16) + '… · ' + t('f_sealed', { t: fmtTime(r.sealMs, off) })]);
    return f;
}

async function makeReport() {
    const sel = selected();
    if (!sel.length) return;
    const btn = $('tscPdfBtn');
    const key = 'set:' + sel.map((r) => r.code).sort().join(',');
    const run = window.__toolLimit ? window.__toolLimit.begin(SLUG, key) : null;
    if (window.__toolLimit && !run) return;
    const st = $('tscRepStatus');
    btn.disabled = true;
    try {
        st.textContent = t('rep_tsa_wait');
        let tsa = null;
        try { tsa = await post(C.urls.report, { seals: sel.map((r) => T.b64urlEncode(r.seal)) }, 20000); } catch (_) { tsa = null; }
        st.textContent = t('rep_building');
        if (!window.PdfTextFont) await window.__vendorScript(C.lazy.pdfTextFont, { expect: 'PdfTextFont', label: 'pdf-text-font' });
        await loadQr();
        const off = sel[0].tzOff;
        const first = sel[0].serverMs, last = sel[sel.length - 1].serverMs;
        const period = fmtDate(first, off) === fmtDate(last, off)
            ? fmtTime(first, off) + '–' + fmtTime(last, off) + ', ' + fmtDate(first, off)
            : fmtTime(first, off) + ' ' + fmtDate(first, off) + ' – ' + fmtTime(last, off) + ' ' + fmtDate(last, off);
        const object = $('tscRepObject').value.trim(), author = $('tscRepAuthor').value.trim();
        const lines = [];
        if (object) lines.push(t('rep_l_object', { v: object }));
        if (author) lines.push(t('rep_l_author', { v: author }));
        lines.push(t('rep_l_period', { n: sel.length, p: period + ' (' + fmtTz(off) + ')' }));
        lines.push(t('rep_sign', { host: C.host, kid: sel[0].capture ? T.decode(sel[0].capture).kid : '' }));
        lines.push(tsa && tsa.ok ? t('rep_tsa_ok', { who: tsa.authority, t: fmtUtc(Date.parse(tsa.time)) }) : t('rep_tsa_none'));
        lines.push(t('rep_how', { url: C.verifyBase }));
        const layout = document.querySelector('#tscRepLayout .tc-seg-b.on');
        const items = sel.map((r) => ({
            bytes: new Uint8Array(0), w: r.w, h: r.h, r,
            label: r.role === 'before' ? t('role_before') : r.role === 'after' ? t('role_after') : '',
            facts: factsFor(r), qrBase: C.verifyBase + '#', qrFrag: T.fragmentFor(r.capture),
        }));
        for (const it of items) it.bytes = new Uint8Array(await it.r.file.arrayBuffer());
        const attachments = [];
        if (tsa && tsa.ok) {
            let n = 0; sel.forEach((r) => { n += r.seal.length; });
            const bin = new Uint8Array(n); let o = 0;
            sel.forEach((r) => { bin.set(r.seal, o); o += r.seal.length; });
            attachments.push({ name: 'stamps.bin', bytes: bin, mime: 'application/octet-stream', desc: 'File seals, in report order' });
            attachments.push({ name: 'timestamp.tsr', bytes: Uint8Array.from(atob(tsa.tsr), (c) => c.charCodeAt(0)), mime: 'application/timestamp-reply', desc: 'RFC 3161 token: ' + tsa.authority });
            attachments.push({ name: 'how-to-verify.txt', bytes: new TextEncoder().encode(t('rep_readme', { url: C.verifyBase, who: tsa.authority, digest: tsa.digest }).replace(/\\n/g, '\n')), mime: 'text/plain', desc: 'How to verify' });
        }
        const title = $('tscRepTitle').value.trim() || t('rep_default_title');
        const bytes = await buildPdf({
            title, lines, rtl: !!C.rtl, items, layout: layout ? layout.dataset.v : 'list',
            footer: t('rep_footer', { host: C.host }), attachments, meta: { title, subject: t('rep_subject') },
        });
        const d = new Date(Date.now() + off * 60000).toISOString();
        const name = 'photo-report-' + d.slice(0, 10) + '-' + d.slice(11, 16).replace(':', '') + '.pdf';
        const ok = await window.__downloadFile(new Blob([bytes], { type: 'application/pdf' }), name);
        if (ok) {
            run && run.commit();
            st.textContent = t('rep_done', { name });
            try { window.__aeToolComplete && window.__aeToolComplete({ kind: 'report', n: sel.length, tsa: tsa && tsa.ok ? 1 : 0, layout: layout ? layout.dataset.v : 'list' }); } catch (_) { }
        } else {
            run && run.abort();
            st.textContent = '';
        }
    } catch (e) {
        run && run.abort();
        st.textContent = '';
        if (window.__handleError) window.__handleError(e, 'tsc_report', { say: t('err_report') });
    } finally {
        btn.disabled = false;
        paintReport();
    }
}

// ── verify ────────────────────────────────────────────────────────────────

let linkStamp = null;

function row(kind, html) { return '<li class="tsc-v ' + kind + '"><span class="tsc-vi" aria-hidden="true">' + ({ ok: '✓', bad: '✕', warn: '!', info: 'i' }[kind]) + '</span><span>' + html + '</span></li>'; }

function captureRows(cap, sig) {
    const out = [];
    if (!sig.known) { out.push(row('warn', esc(t('v_sig_unknown', { kid: cap.kid })))); return out; }
    if (!sig.valid) { out.push(row('bad', esc(t('v_sig_bad')))); return out; }
    out.push(row('ok', esc(t('v_sig_ok', { kid: cap.kid }))));
    const off = cap.tzOff;
    const tm = fmtTime(cap.serverMs, off) + ' · ' + fmtDate(cap.serverMs, off) + ' (' + fmtTz(off) + ')';
    if (cap.gallery) out.push(row('ok', esc(t('v_time_gallery', { t: tm }))));
    else if (cap.late) out.push(row('ok', esc(t('v_time_late', { t: tm, d: fmtTime(cap.deviceMs, off) + ' ' + fmtDate(cap.deviceMs, off) }))));
    else out.push(row('ok', esc(t('v_time_live', { t: tm }))));
    const mine = -new Date(cap.serverMs).getTimezoneOffset();
    if (mine !== off) out.push(row('info', esc(t('v_your_time', { t: fmtTime(cap.serverMs, mine) + ' · ' + fmtDate(cap.serverMs, mine) + ' (' + fmtTz(mine) + ')' }))));
    if (!cap.gallery) out.push(row(Math.abs(cap.skewMs) >= BIG_SKEW_MS ? 'warn' : 'info', esc(t('v_phone', { s: skewText(cap.skewMs) }))));
    if (cap.exifMs != null) {
        const e = new Date(cap.exifMs).toISOString();
        out.push(row('info', esc(t('v_exif', { e: e.slice(8, 10) + '.' + e.slice(5, 7) + '.' + e.slice(0, 4) + ' ' + e.slice(11, 16) + (cap.exifOff != null ? ' ' + fmtTz(cap.exifOff) : '') }))));
    }
    if (cap.lat != null) {
        const map = 'https://www.openstreetmap.org/?mlat=' + cap.lat.toFixed(5) + '&mlon=' + cap.lon.toFixed(5) + '#map=17/' + cap.lat.toFixed(5) + '/' + cap.lon.toFixed(5);
        const p = coords({ lat: cap.lat, lon: cap.lon, acc: cap.acc });
        out.push(row('info', esc(t(cap.gallery ? 'v_place_file' : 'v_place', { p })) + ' <a href="' + esc(map) + '" target="_blank" rel="noopener nofollow">' + esc(t('v_map')) + '</a>'));
    } else out.push(row('info', esc(t('v_no_place'))));
    out.push(row('info', esc(t('v_code', { c: cap.code }))));
    return out;
}

function visualRow(v) {
    if (!v || v.d == null) return row('warn', esc(t('v_vis_flat')));
    if (v.d <= V.SAME) return row('ok', esc(t('v_vis_same', { d: v.d })));
    if (v.d <= V.CLOSE) return row('warn', esc(t('v_vis_close', { d: v.d })));
    return row('bad', esc(t('v_vis_diff', { d: v.d })));
}

function renderImage(res) {
    const out = [];
    if (res.exact) {
        if (res.seal) {
            if (!res.sealSig || !res.sealSig.valid) out.push(row('bad', esc(t('v_seal_bad'))));
            else if (res.fileMatch && res.sameCode) out.push(row('ok', esc(t('v_file_ok', { t: fmtUtc(res.seal.serverMs) }))));
            else if (!res.sameCode) out.push(row('bad', esc(t('v_code_mismatch'))));
            else out.push(row('bad', esc(t('v_file_bad'))));
        }
        if (res.capture && res.captureSig) out.push(...captureRows(res.capture, res.captureSig));
        if (res.visual && !res.fileMatch) out.push(visualRow(res.visual));
    } else if (res.capture) {
        out.push(row('info', esc(t(res.fromLink ? 'v_copy_link' : 'v_copy'))));
        out.push(visualRow(res.visual));
        out.push(...captureRows(res.capture, res.captureSig));
    } else {
        out.push(row('warn', esc(t(res.qrFailed ? 'v_qr_engine' : 'v_no_stamp'))));
    }
    return '<ul class="tsc-vlist">' + out.join('') + '</ul>';
}

function renderPdf(res) {
    if (!res.photos.length) return '<ul class="tsc-vlist">' + row('warn', esc(t('v_pdf_none'))) + '</ul>';
    let h = '<p class="tsc-vh">' + esc(t('v_pdf_title', { n: res.photos.length })) + '</p>';
    if (res.tsa) {
        h += '<ul class="tsc-vlist">' + (res.tsa.ok && res.tsa.sameSet ? row('ok', esc(t('v_tsa_ok', { t: res.tsa.time ? fmtUtc(Date.parse(res.tsa.time)) : '' })))
            : !res.tsa.lib ? row('warn', esc(t('v_tsa_lib'))) : row('bad', esc(t('v_tsa_bad')))) + '</ul>';
    } else h += '<ul class="tsc-vlist">' + row('info', esc(t('v_tsa_none'))) + '</ul>';
    res.photos.forEach((p, i) => {
        h += '<p class="tsc-vh sub">' + esc(t('v_pdf_photo', { i: i + 1, code: p.capture ? p.capture.code : (p.seal ? p.seal.code : '') })) + '</p>';
        h += renderImage(Object.assign({ exact: true }, p));
    });
    return h;
}

async function checkFile(file) {
    const out = $('tscVerifyOut');
    out.innerHTML = '<p class="tc-hint">' + esc(t('v_checking')) + '</p>';
    stage('verify', { kind: /pdf/i.test(file.type) || /\.pdf$/i.test(file.name) ? 'pdf' : 'image' });
    try {
        const keys = C.keys || {};
        if (/pdf/i.test(file.type) || /\.pdf$/i.test(file.name)) {
            const PDFLib = await loadPdfLib();
            if (!window.CCVerify) { try { await window.__vendorScript(C.lazy.ccVerify, { expect: 'CCVerify', label: 'cc-verify' }); } catch (_) { } }
            out.innerHTML = renderPdf(await V.checkPdf(file, keys, serverVerify, PDFLib));
        } else {
            // qr-decode fetches its own ZXing engine (self-hosted wasm) on first use
            if (!window.__qrDecode) { try { await window.__vendorScript(C.lazy.qrDecode, { expect: '__qrDecode', label: 'qr-decode' }); } catch (_) { } }
            out.innerHTML = renderImage(await V.checkImage(file, keys, serverVerify, linkStamp));
        }
    } catch (e) {
        out.innerHTML = '<ul class="tsc-vlist">' + row('bad', esc(t('v_err'))) + '</ul>';
        if (window.__handleError) window.__handleError(e, 'tsc_verify', { say: t('v_err') });
    }
}

async function fromLink() {
    const box = $('tscLink');
    if (!/^#V1/i.test(location.hash)) { box.hidden = true; return; }
    const res = await V.fromLink(location.hash, C.keys || {}, serverVerify);
    if (!res) { box.hidden = false; box.innerHTML = '<ul class="tsc-vlist">' + row('bad', esc(t('v_link_bad'))) + '</ul>'; return; }
    linkStamp = res.captureSig.valid ? res.capture : null;
    box.hidden = false;
    box.innerHTML = '<h3 class="tc-title">' + esc(t('v_title_link')) + '</h3><ul class="tsc-vlist">' + captureRows(res.capture, res.captureSig).join('') + '</ul>'
        + '<p class="tc-hint">' + esc(t('v_drop_to_compare')) + '</p>';
    stage('verify', { kind: 'link', valid: res.captureSig.valid ? 1 : 0 });
    setTab('verify');
}

// ── tabs and wiring ───────────────────────────────────────────────────────

function setTab(name) {
    document.querySelectorAll('#tscTabs .tc-seg-b').forEach((b) => {
        const on = b.dataset.tab === name;
        b.classList.toggle('on', on);
        b.setAttribute('aria-selected', on ? 'true' : 'false');
        b.tabIndex = on ? 0 : -1;
    });
    document.querySelectorAll('.tsc-pane').forEach((p) => { p.hidden = p.dataset.pane !== name; });
}

function onRollClick(e) {
    const btn = e.target.closest('[data-act]');
    if (!btn) return;
    const cell = btn.closest('.tsc-cell');
    const r = cell && recs.find((x) => x.id === cell.dataset.id);
    if (!r) return;
    const act = btn.dataset.act;
    if (act === 'sel') { r.sel = !r.sel; saveRec(r); renderRoll(); }
    else if (act === 'role') { r.role = r.role === btn.dataset.v ? '' : btn.dataset.v; saveRec(r); renderRoll(); }
    else if (act === 'dl' && r.file) { window.__downloadFile(r.file, fileName(r)); }
    else if (act === 'retry') { r.status = 'pending'; confirm(r); }
    else if (act === 'del') { recs = recs.filter((x) => x !== r); roll.remove(r.id); renderRoll(); }
    else if (act === 'note') openNote(r);
}

let noteFor = null;
function openNote(r) {
    noteFor = r;
    $('tscNoteText').value = r.note || '';
    const d = $('tscNoteDlg');
    if (d.showModal) d.showModal(); else d.setAttribute('open', '');
}

function bindSettings() {
    const s = window.__toolSettings;
    if (!s) return;
    s.bind(SLUG, $('tscObject'), 'object', '');
    s.bind(SLUG, $('tscOptPlace'), 'place', true);
    s.bind(SLUG, $('tscOptWeather'), 'weather', true);
    s.bind(SLUG, $('tscOptQr'), 'qr', true);
    s.bind(SLUG, $('tscRepAuthor'), 'author', '');
}

function init() {
    bindSettings();
    $('tscRepObject').value = $('tscObject').value;
    $('tscTabs').addEventListener('click', (e) => { const b = e.target.closest('.tc-seg-b'); if (b) setTab(b.dataset.tab); });
    $('tscTabs').addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        const bs = Array.from(document.querySelectorAll('#tscTabs .tc-seg-b'));
        const i = bs.findIndex((b) => b.classList.contains('on'));
        const n = bs[(i + ((e.key === 'ArrowRight') !== !!C.rtl ? 1 : bs.length - 1)) % bs.length];
        setTab(n.dataset.tab); n.focus();
    });
    $('tscStart').addEventListener('click', (e) => openCamera(e.currentTarget));
    $('tscRetry').addEventListener('click', (e) => openCamera(e.currentTarget));
    $('tscShutter').addEventListener('click', shoot);
    $('tscFlip').addEventListener('click', () => { facing = facing === 'environment' ? 'user' : 'environment'; openCamera($('tscFlip')); });
    $('tscLast').addEventListener('click', () => { const r = recs[recs.length - 1]; if (r && r.file) window.__downloadFile(r.file, fileName(r)); });
    document.addEventListener('keydown', (e) => {
        if ((e.key === ' ' || e.key === 'Enter') && !(window.__keyOwnedByFocus && window.__keyOwnedByFocus(e))
            && !$('tscShutter').disabled && !document.querySelector('.tsc-pane[data-pane="shoot"]').hidden) { e.preventDefault(); shoot(); }
    });
    $('tscOptPlace').addEventListener('change', startGeo);
    $('tscObject').addEventListener('change', () => { if (!$('tscRepObject').value) $('tscRepObject').value = $('tscObject').value; });
    $('tscGrid').addEventListener('click', onRollClick);
    $('tscSelAll').addEventListener('click', () => {
        const all = recs.length && recs.every((r) => r.sel);
        recs.forEach((r) => { r.sel = !all; roll.put(r); });
        renderRoll();
    });
    $('tscDelAll').addEventListener('click', async () => {
        if (!window.confirm(t('del_confirm'))) return;
        recs = []; await roll.clear(); renderRoll();
    });
    $('tscDlAll').addEventListener('click', async () => {
        const items = recs.filter((r) => r.status === 'ok' && r.file).map((r) => ({ name: fileName(r), blob: r.file }));
        if (!items.length) return;
        if (window.__batchDeliver) { try { await window.__batchDeliver.deliver(items, { zipName: 'photos.zip', slug: SLUG }); } catch (e) { window.__handleError && window.__handleError(e, 'tsc_zip', { say: t('err_report') }); } }
        else if (items.length === 1) window.__downloadFile(items[0].blob, items[0].name);
    });
    $('tscNoteSave').addEventListener('click', () => {
        if (noteFor) { noteFor.note = $('tscNoteText').value.trim().slice(0, 500); saveRec(noteFor); renderRoll(); }
        $('tscNoteDlg').close ? $('tscNoteDlg').close() : $('tscNoteDlg').removeAttribute('open');
    });
    $('tscNoteCancel').addEventListener('click', () => { $('tscNoteDlg').close ? $('tscNoteDlg').close() : $('tscNoteDlg').removeAttribute('open'); });
    document.querySelectorAll('#tscRepLayout .tc-seg-b').forEach((b) => b.addEventListener('click', () => {
        document.querySelectorAll('#tscRepLayout .tc-seg-b').forEach((x) => { x.classList.toggle('on', x === b); x.setAttribute('aria-pressed', x === b ? 'true' : 'false'); });
        try { window.__toolSettings && window.__toolSettings.save(SLUG, 'layout', b.dataset.v); } catch (_) { }
    }));
    try {
        const lay = window.__toolSettings && window.__toolSettings.load(SLUG, 'layout', 'list');
        const b = document.querySelector('#tscRepLayout .tc-seg-b[data-v="' + lay + '"]');
        if (b) b.click();
    } catch (_) { }
    $('tscPdfBtn').addEventListener('click', makeReport);

    const gal = $('tscGallery');
    const onGallery = (files) => { if (files && files.length) addFromGallery(Array.from(files)); };
    if (window.__bindUploaderFile) window.__bindUploaderFile(gal, (f, d) => onGallery((d && d.files && d.files.length) ? d.files : [f]));
    else gal.addEventListener('tool-uploader:file', (e) => onGallery(e.detail.files || [e.detail.file]));
    const vd = $('tscVerifyDrop');
    const onVerify = (f) => { if (f) checkFile(f); };
    if (window.__bindUploaderFile) window.__bindUploaderFile(vd, (f) => onVerify(f));
    else vd.addEventListener('tool-uploader:file', (e) => onVerify(e.detail.file || (e.detail.files && e.detail.files[0])));

    window.addEventListener('hashchange', fromLink);
    window.addEventListener('online', retryPending);
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') { if (Date.now() - clock.at > 60000) ping(); retryPending(); }
    });
    window.addEventListener('pagehide', () => { try { cam && cam.close(); } catch (_) { } stopGeo(); });
    setInterval(() => { if (document.visibilityState === 'visible') paintClock(); }, 1000);
    setInterval(() => { if (document.visibilityState === 'visible') ping(); }, 5 * 60000);

    ping();
    loadRoll();
    fromLink();
    paintGeo();
    document.getElementById('tscApp').dataset.ready = '1';
    // read-only window for the e2e stand (tests/playwright/timestamp-camera-e2e.mjs)
    window.__tscDebug = { get recs() { return recs; }, get clock() { return clock; } };
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
else init();
